<template>
    <v-dialog v-model="openMode" max-width="780px" content-class="mm-dialog" overlay-color="#0d2c6d"
        overlay-opacity="0.55" transition="dialog-bottom-transition">
        <v-card class="mm-dlg-card">
            <div class="mm-dlg-head">
                <div class="mm-dlg-head__main">
                    <div class="mm-dlg-head__icon" aria-hidden="true">
                        <v-icon color="white">mdi-package-variant-closed</v-icon>
                    </div>
                    <div>
                        <span class="mm-dlg-eyebrow">חבילת מנוי</span>
                        <h2 class="mm-dlg-title">{{ formTitle }}</h2>
                    </div>
                </div>
                <div class="mm-dlg-head__pill" v-if="itemEdited.plan_id && itemEdited.plan_id.name">
                    <v-icon small color="white">mdi-tag-outline</v-icon>
                    {{ itemEdited.plan_id.name }}
                </div>
                <button type="button" class="mm-dlg-close" @click="openMode = false" aria-label="סגור">
                    <v-icon small color="white">mdi-close</v-icon>
                </button>
            </div>

            <v-card-text class="mm-dlg-body">
                <!-- ─────────── CURRENT PACKS + USAGE ─────────── -->
                <div class="mm-dlg-section">
                    <h3 class="mm-dlg-section__title">
                        <span class="mm-dlg-section__bar"></span>
                        חבילות קיימות במנוי
                        <span class="mm-packs-count" v-if="!packsLoading && normalizedPacks.length">
                            {{ normalizedPacks.length }}
                        </span>
                    </h3>

                    <!-- loading -->
                    <div v-if="packsLoading" class="mm-packs-loading">
                        <v-progress-circular indeterminate size="34" width="3" color="#0d2c6d" />
                        <span>טוען את נתוני החבילות…</span>
                    </div>

                    <!-- empty -->
                    <div v-else-if="!normalizedPacks.length" class="mm-dlg-empty">
                        <v-icon size="34" color="#94a3b8">mdi-package-variant-remove</v-icon>
                        <div class="mm-packs-empty__text">לא נמצאו חבילות פעילות למנוי זה</div>
                    </div>

                    <!-- packs grid -->
                    <div v-else class="mm-packs-grid">
                        <article v-for="pack in normalizedPacks" :key="pack.id" class="mm-pack"
                            :class="{ 'mm-pack--off': !pack.isActive }">

                            <header class="mm-pack__head">
                                <div class="mm-pack__icon" :class="`mm-pack__icon--${pack.kind}`">
                                    <v-icon size="18">{{ pack.icon }}</v-icon>
                                </div>
                                <div class="mm-pack__titles">
                                    <h4 class="mm-pack__name" :title="pack.name">{{ pack.name }}</h4>
                                    <span class="mm-pack__sub" v-if="pack.subName" :title="pack.subName">
                                        {{ pack.subName }}
                                    </span>
                                </div>
                                <span class="mm-pack__status"
                                    :class="pack.isActive ? 'mm-pack__status--on' : 'mm-pack__status--off'">
                                    <span class="mm-pack__status-dot"></span>
                                    {{ pack.isActive ? 'פעילה' : 'כבויה' }}
                                </span>
                            </header>

                            <div class="mm-pack__meter" v-if="pack.hasQuota">
                                <div class="mm-pack__meter-top">
                                    <div class="mm-pack__remain">
                                        <strong class="mm-pack__remain-num">{{ pack.freeLabel }}</strong>
                                        <span class="mm-pack__remain-unit">{{ pack.unit }} נותרו</span>
                                    </div>
                                    <span class="mm-pack__pct" :class="`mm-pack__pct--${pack.level}`">
                                        {{ pack.pctLabel }}%
                                    </span>
                                </div>

                                <div class="mm-pack__bar" role="progressbar" :aria-valuenow="pack.freePct"
                                    aria-valuemin="0" aria-valuemax="100">
                                    <div class="mm-pack__bar-fill" :class="`mm-pack__bar-fill--${pack.level}`"
                                        :style="{ width: pack.freePct + '%' }"></div>
                                </div>

                                <div class="mm-pack__meter-foot">
                                    <span v-if="pack.isOverQuota">
                                        <v-icon size="13">mdi-arrow-up-bold-circle-outline</v-icon>
                                        מכסת החבילה {{ pack.totalLabel }} · יתרה חורגת
                                    </span>
                                    <span v-else>
                                        <v-icon size="13">mdi-chart-donut</v-icon>
                                        נוצלו {{ pack.usedLabel }} מתוך {{ pack.totalLabel }}
                                    </span>
                                    <span v-if="pack.isOverQuota" class="mm-pack__bonus">
                                        <v-icon size="12">mdi-plus</v-icon>
                                        {{ pack.bonusLabel }} {{ pack.unit }} מעל המכסה
                                    </span>
                                </div>
                            </div>

                            <div class="mm-pack__meter mm-pack__meter--none" v-else>
                                <v-icon size="15" color="#94a3b8">mdi-infinity</v-icon>
                                <span>ללא מכסה מוגדרת</span>
                            </div>

                            <footer class="mm-pack__foot">
                                <span class="mm-pack__chip">
                                    <v-icon size="13">mdi-calendar-refresh-outline</v-icon>
                                    {{ pack.renewLabel }}
                                </span>
                                <button v-if="isAdmin && pack.kind === 'call'" type="button" class="mm-min-trigger"
                                    :class="{ 'mm-min-trigger--open': minutesPanelId === pack.id }"
                                    @click="toggleMinutesPanel(pack)">
                                    <v-icon size="14">mdi-timer-cog-outline</v-icon>
                                    עדכון דקות
                                </button>
                            </footer>

                            <!-- ── פאנל עדכון דקות (אדמין) ── -->
                            <v-expand-transition>
                                <div v-if="isAdmin && minutesPanelId === pack.id" class="mm-min">
                                    <div class="mm-min__seg" role="group" aria-label="סוג הפעולה">
                                        <button type="button" class="mm-min__seg-btn"
                                            :class="{ 'is-on': minutesAction === 'add' }" :disabled="minutesSaving"
                                            @click="minutesAction = 'add'">
                                            <v-icon size="15">mdi-plus-circle-outline</v-icon>
                                            הוספה
                                        </button>
                                        <button type="button" class="mm-min__seg-btn mm-min__seg-btn--remove"
                                            :class="{ 'is-on': minutesAction === 'remove' }" :disabled="minutesSaving"
                                            @click="minutesAction = 'remove'">
                                            <v-icon size="15">mdi-minus-circle-outline</v-icon>
                                            הסרה
                                        </button>
                                    </div>

                                    <div class="mm-min__stepper">
                                        <button type="button" class="mm-min__step" :disabled="minutesSaving"
                                            @click="stepMinutes(-50)" aria-label="הפחת 50">
                                            <v-icon size="18">mdi-minus</v-icon>
                                        </button>
                                        <div class="mm-min__field">
                                            <input v-model.number="minutesValue" type="number" min="1"
                                                inputmode="numeric" :disabled="minutesSaving" aria-label="מספר דקות" />
                                            <span class="mm-min__field-unit">דקות</span>
                                        </div>
                                        <button type="button" class="mm-min__step" :disabled="minutesSaving"
                                            @click="stepMinutes(50)" aria-label="הוסף 50">
                                            <v-icon size="18">mdi-plus</v-icon>
                                        </button>
                                    </div>

                                    <div class="mm-min__quick">
                                        <button v-for="q in [50, 100, 250, 500]" :key="q" type="button"
                                            class="mm-min__chip" :class="{ 'is-on': minutesValue === q }"
                                            :disabled="minutesSaving" @click="minutesValue = q">
                                            {{ q }}
                                        </button>
                                    </div>

                                    <div class="mm-min__preview"
                                        :class="minutesAction === 'add' ? 'mm-min__preview--add' : 'mm-min__preview--remove'">
                                        <v-icon size="15">
                                            {{ minutesAction === 'add' ? 'mdi-trending-up' : 'mdi-trending-down' }}
                                        </v-icon>
                                        <span>
                                            היתרה תעודכן מ־<strong>{{ pack.freeLabel }}</strong>
                                            ל־<strong>{{ formatNumber(previewFree(pack)) }}</strong> {{ pack.unit }}
                                            <em v-if="previewOverQuota(pack) > 0" class="mm-min__preview-note">
                                                ({{ formatNumber(previewOverQuota(pack)) }} מעל מכסת החבילה)
                                            </em>
                                        </span>
                                    </div>

                                    <div class="mm-min__actions">
                                        <button type="button" class="mm-min__btn mm-min__btn--ghost"
                                            :disabled="minutesSaving" @click="closeMinutesPanel">
                                            ביטול
                                        </button>
                                        <button type="button" class="mm-min__btn"
                                            :class="minutesAction === 'add' ? 'mm-min__btn--add' : 'mm-min__btn--remove'"
                                            :disabled="minutesSaving || !minutesValue || minutesValue <= 0"
                                            @click="changePackMinutes(pack)">
                                            <v-progress-circular v-if="minutesSaving" indeterminate size="14" width="2"
                                                color="#fff" class="ml-1" />
                                            {{ minutesAction === 'add' ? 'הוסף דקות' : 'הסר דקות' }}
                                        </button>
                                    </div>
                                </div>
                            </v-expand-transition>
                        </article>
                    </div>
                </div>

                <v-form @submit.prevent="submitHandler" ref="form">
                    <div class="mm-dlg-section">
                        <h3 class="mm-dlg-section__title">
                            <span class="mm-dlg-section__bar"></span>
                            בחירת חבילה חדשה
                        </h3>
                        <p class="mm-dlg-section__hint">מוצגות רק חבילות שמתאימות למפעיל הנוכחי של המנוי.</p>
                        <v-row>
                            <v-col cols="12" sm="8">
                                <v-select :items="filteredPlans" :item-text="'name'" return-object hide-details
                                    v-model="planChoosed" outlined dense label="בחר חבילה חדשה"
                                    prepend-inner-icon="mdi-format-list-bulleted" required
                                    :rules="[v => !!v || 'שדה חובה']">
                                </v-select>
                            </v-col>
                        </v-row>
                    </div>
                </v-form>
            </v-card-text>

            <div class="mm-dlg-foot">
                <v-progress-linear v-if="progressShow" indeterminate color="#0d2c6d" height="3"
                    class="mm-dlg-progress" />
                <div class="mm-dlg-foot__actions">
                    <button type="button" class="mm-dlg-btn mm-dlg-btn--ghost" @click="openMode = false">
                        <v-icon small>mdi-close</v-icon>
                        ביטול
                    </button>
                    <button type="button" class="mm-dlg-btn mm-dlg-btn--primary"
                        :disabled="!planChoosed?._id || planChoosed?._id === itemToEdit.plan_id._id || progressShow"
                        @click="updatePlan()">
                        <v-icon small color="white">mdi-check</v-icon>
                        עדכן
                    </button>
                </div>
            </div>
        </v-card>

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
    </v-dialog>
</template>

<script>
import SnackBar from '@/components/widgets/snackBar.vue'
import ApiServices from '@/services/api.service'
import SharedFunctions from '@/shared/SharedFunctions';
import Auth from '@/services/auth.service';

export default {
    props: {
        itemToEdit: Object,
        formTitle: String,
        value: { type: Boolean, default: false },
    },
    components: {
        SnackBar,
    },
    data: () => ({
        progressShow: false,
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",
        itemEdited: {
            _id: "",
            manui_number: "",
            status: "",
        },
        planChoosed: {},
        plans: [],
        manuiPacks: [],
        packsLoading: true,
        role: Auth.getUserRole(),
        minutesPanelId: null,
        minutesAction: 'add',
        minutesValue: 100,
        minutesSaving: false,
    }),
    methods: {
        async updatePlan() {
            if (this.$refs.form.validate()) {
                const myJSON = JSON.stringify({
                    packNumToAdd: this.planChoosed.pack_id, manuiNumber: this.itemEdited.manui_number, from: "website"
                });
                this.progressShow = true;
                let token = localStorage.getItem("token");
                try {
                    let api = process.env.VUE_APP_BASE_URL + "/manuim/edit_manui_plan";
                    const res = await fetch(api, ApiServices.requestOptions("PUT", myJSON, token));
                    this.progressShow = false;
                    const jsonObject = await res.json();
                    if (res.status >= 400) {
                        this.showSnackBar("Error: " + jsonObject, "red");
                    } else if (res.status === 200) {
                        if (!jsonObject.status) {
                            return this.showSnackBar("שגיאה בעדכון החבילה, הוראת קבע בנקאית", "red");
                        }
                        this.itemEdited.plan_id = this.planChoosed;
                        this.$emit("itemEdited", this.itemEdited, "החבילה עודכנה בהצלחה", "green");
                        this.openMode = false;
                    }
                } catch (error) {
                    this.progressShow = false;
                    this.showSnackBar("Error saved edited plan: " + error, "red");
                }
            }
        },
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true
        },
        async getPlans() {
            this.plans = await SharedFunctions.getPlans();
            try {
                this.packsLoading = true;
                const res = await SharedFunctions.getManuiPacks(this.itemEdited.manui_number);
                // Accept either a plain array or a wrapper object such as { data: [...] }
                this.manuiPacks = Array.isArray(res) ? res : ((res && res.data) || []);
            } catch (error) {
                this.manuiPacks = [];
            } finally {
                this.packsLoading = false;
            }
        },
        // ── Pack display helpers ──
        unitLabel(billingType) {
            const map = {
                minute: 'דקות',
                minutes: 'דקות',
                sms: 'הודעות',
                data: 'MB',
                mb: 'MB',
                gb: 'GB',
            };
            return map[String(billingType || '').toLowerCase()] || 'יחידות';
        },
        packIcon(billingType) {
            const map = {
                minute: 'mdi-phone-outline',
                minutes: 'mdi-phone-outline',
                sms: 'mdi-message-text-outline',
                data: 'mdi-web',
                mb: 'mdi-web',
                gb: 'mdi-web',
            };
            return map[String(billingType || '').toLowerCase()] || 'mdi-package-variant-closed';
        },
        packKind(billingType) {
            const t = String(billingType || '').toLowerCase();
            if (t === 'minute' || t === 'minutes' || t === 'prefix_minute') return 'call';
            if (t === 'sms') return 'sms';
            if (t === 'data' || t === 'mb' || t === 'gb') return 'data';
            return 'other';
        },
        formatNumber(value) {
            const num = Number(value);
            if (!isFinite(num)) return '—';
            // pnoi arrives as "1500.000" - drop meaningless decimals
            const rounded = Math.round(num * 100) / 100;
            return rounded.toLocaleString('he-IL');
        },
        renewLabel(autoRenew) {
            const raw = String(autoRenew == null ? '' : autoRenew).trim();
            if (!raw || raw === '0') return 'ללא חידוש אוטומטי';
            const day = Number(raw);
            if (isFinite(day) && day >= 1 && day <= 31) {
                return `מתחדשת ב-${day} לחודש`;
            }
            return `חידוש: ${raw}`;
        },
        // ── עדכון דקות לחבילה (אדמין) ──
        toggleMinutesPanel(pack) {
            if (this.minutesPanelId === pack.id) {
                this.closeMinutesPanel();
                return;
            }
            this.minutesPanelId = pack.id;
            this.minutesAction = 'add';
            this.minutesValue = 100;
        },
        closeMinutesPanel() {
            this.minutesPanelId = null;
            this.minutesSaving = false;
        },
        stepMinutes(delta) {
            const next = Number(this.minutesValue || 0) + delta;
            this.minutesValue = Math.max(0, Math.min(100000, next));
        },
        previewFree(pack) {
            const minutes = Number(this.minutesValue) || 0;
            const delta = this.minutesAction === 'add' ? minutes : -minutes;
            return Math.max(0, (pack.freeRaw || 0) + delta);
        },
        previewOverQuota(pack) {
            if (pack.totalRaw === null) return 0;
            return Math.max(0, this.previewFree(pack) - pack.totalRaw);
        },
        applyMinutesLocally(pack, minutes, action) {
            const raw = this.manuiPacks.find(p => String(p.id) === String(pack.id));
            if (!raw) return;
            // allk is the package quota and stays fixed - only the remaining
            // balance (pnoi) changes, and it may end up above the quota
            const delta = action === 'add' ? minutes : -minutes;
            const free = Number(raw.pnoi);
            if (isFinite(free)) this.$set(raw, 'pnoi', Math.max(0, free + delta));
        },
        async changePackMinutes(pack) {
            const minutes = Number(this.minutesValue);
            const action = this.minutesAction;

            if (!isFinite(minutes) || minutes <= 0) {
                return this.showSnackBar("יש להזין מספר דקות גדול מאפס", "red");
            }
            if (action === 'remove' && pack.freeRaw !== null && minutes > pack.freeRaw) {
                return this.showSnackBar(`אפשר להסיר עד ${pack.freeLabel} דקות`, "red");
            }

            try {
                this.minutesSaving = true;
                const token = localStorage.getItem("token");
                const myJSON = JSON.stringify({
                    packId: pack.id,
                    manuiId: this.itemEdited._id,
                    actionType: action,
                    minutes,
                });

                let api = process.env.VUE_APP_BASE_URL + "/manuim/change_pack_minutes";
                const res = await fetch(api, ApiServices.requestOptions("POST", myJSON, token));
                const jsonObject = await res.json().catch(() => ({}));
                this.minutesSaving = false;

                if (res.status >= 400) {
                    return this.showSnackBar("שגיאה בעדכון הדקות: " + (jsonObject.message || ""), "red");
                }

                this.applyMinutesLocally(pack, minutes, action);
                this.closeMinutesPanel();
                this.showSnackBar(
                    action === 'add'
                        ? `נוספו ${this.formatNumber(minutes)} דקות לחבילה`
                        : `הוסרו ${this.formatNumber(minutes)} דקות מהחבילה`,
                    "green"
                );
            } catch (error) {
                this.minutesSaving = false;
                this.showSnackBar("Error change pack minutes: " + error, "red");
            }
        },
    },
    computed: {
        filteredPlans() {
            return this.plans.filter((plan) => plan.operating_company === this.itemEdited.plan_id.operating_company);
        },

        normalizedPacks() {
            const packs = Array.isArray(this.manuiPacks) ? this.manuiPacks : [];

            return packs
                .map((p) => {
                    const total = Number(p.allk);
                    const free = Number(p.pnoi);
                    const hasQuota = isFinite(total) && total > 0;

                    // pnoi may legitimately exceed allk when minutes were granted on top
                    // of the package quota - do NOT clamp it to the quota
                    const safeFree = isFinite(free) ? Math.max(0, free) : 0;
                    const isOverQuota = hasQuota && safeFree > total;
                    const bonus = isOverQuota ? safeFree - total : 0;
                    const used = hasQuota ? Math.max(0, total - safeFree) : 0;
                    const rawPct = hasQuota ? Math.round((safeFree / total) * 100) : 0;
                    const freePct = Math.min(100, rawPct); // bar width only

                    // level drives the bar colour: over quota / plenty left / running low / nearly out
                    let level = 'good';
                    if (isOverQuota) level = 'bonus';
                    else if (freePct <= 15) level = 'low';
                    else if (freePct <= 40) level = 'mid';

                    const isActive = String(p.enable) === '1';
                    if (!isActive) level = 'off';

                    return {
                        id: p.id,
                        // pack_name is the operator-facing name, name is the descriptive one
                        name: p.pack_name || p.name || 'חבילה',
                        subName: p.pack_name && p.name && p.pack_name !== p.name ? p.name : '',
                        isActive,
                        hasQuota,
                        freePct,
                        pctLabel: rawPct,
                        isOverQuota,
                        bonusLabel: this.formatNumber(bonus),
                        level,
                        unit: this.unitLabel(p.billing_type),
                        icon: this.packIcon(p.billing_type),
                        kind: this.packKind(p.billing_type),
                        freeLabel: this.formatNumber(safeFree),
                        usedLabel: this.formatNumber(used),
                        totalLabel: this.formatNumber(total),
                        renewLabel: this.renewLabel(p.auto_renew),
                        totalRaw: hasQuota ? total : null,
                        freeRaw: safeFree,
                    };
                })
                // active packs first, then by most-depleted so problems surface at the top
                .sort((a, b) => {
                    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
                    return a.freePct - b.freePct;
                });
        },

        isAdmin() {
            return this.role === 'admin';
        },

        openMode: {
            get() {
                return this.value
            },
            set(v) {
                return this.$emit('input', v)
            }
        },
    },
    mounted() {
        // itemEdited must be populated BEFORE getPlans(), because getManuiPacks
        // depends on this.itemEdited.manui_number (previously it was called with "")
        this.itemEdited = JSON.parse(JSON.stringify(this.itemToEdit));
        this.planChoosed = this.itemEdited.plan_id;
        this.getPlans();
    },
}
</script>
<style scoped>
.mm-dialog {
    border-radius: 20px !important;
    overflow: hidden;
}

.mm-dlg-card {
    --mm-navy: #0d2c6d;
    --mm-indigo: #3F51B5;
    --mm-purple: #8066f0;
    --mm-gold: #fdb721;
    --mm-border: #eef2f7;
    --mm-text: #0f172a;
    --mm-text-soft: #475569;
    --mm-text-mute: #94a3b8;
    font-family: 'Assistant', 'Heebo', system-ui, sans-serif;
    direction: rtl;
    border-radius: 20px !important;
    overflow: hidden;
    background: #ffffff !important;
}

.mm-dlg-head {
    position: relative;
    background:
        radial-gradient(800px 200px at 90% -40%, rgba(253, 183, 33, 0.08), transparent 60%),
        linear-gradient(135deg, #0d2c6d 0%, #142f78 55%, #1a3a8a 100%);
    color: #fff;
    padding: 18px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.mm-dlg-head__main {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
}

.mm-dlg-head__icon {
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    border-radius: 11px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.22);
    display: grid;
    place-items: center;
    color: #fff;
}

.mm-dlg-eyebrow {
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.65);
    margin-bottom: 1px;
}

.mm-dlg-title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mm-dlg-head__pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 34px;
    padding: 7px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: rgba(255, 255, 255, 0.88);
    font-size: 0.82rem;
    font-weight: 700;
    white-space: nowrap;
}

.mm-dlg-close {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: #fff;
    cursor: pointer;
    display: grid;
    place-items: center;
    transition: background 0.2s, transform 0.2s;
}

.mm-dlg-close:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: rotate(90deg);
}

.mm-dlg-body {
    padding: 24px 28px 8px !important;
    background: #fbfcfe;
    max-height: 65vh;
    overflow-y: auto;
}

.mm-dlg-section {
    margin-bottom: 18px;
    padding: 16px 18px 6px;
    background: #ffffff;
    border: 1px solid var(--mm-border);
    border-radius: 14px;
    box-shadow: 0 12px 28px -24px rgba(15, 23, 42, 0.35);
}

.mm-dlg-section__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.92rem;
    font-weight: 700;
    color: var(--mm-navy);
    margin: 0 0 14px;
}

.mm-dlg-section__bar {
    width: 3px;
    height: 16px;
    background: linear-gradient(180deg, var(--mm-navy), var(--mm-purple));
    border-radius: 2px;
}

.mm-dlg-section__hint {
    color: var(--mm-text-soft);
    font-size: 0.86rem;
    line-height: 1.7;
    margin: -4px 0 14px;
}

.mm-dlg-info-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    margin-bottom: 14px;
    border-radius: 12px;
    background: #f8fafc;
    border: 1px solid #e3e8ef;
    color: var(--mm-text-soft);
    font-weight: 700;
}

.mm-dlg-empty {
    text-align: center;
    color: var(--mm-text-mute);
    padding: 28px 10px;
    font-weight: 700;
}

/* ─────────────── PACKS: header / states ─────────────── */
.mm-packs-count {
    display: inline-grid;
    place-items: center;
    min-width: 22px;
    height: 22px;
    padding: 0 7px;
    border-radius: 999px;
    background: rgba(13, 44, 109, 0.08);
    color: var(--mm-navy);
    font-size: 0.75rem;
    font-weight: 800;
}

.mm-packs-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 30px 10px;
    color: var(--mm-text-soft);
    font-weight: 700;
    font-size: 0.88rem;
}

.mm-packs-empty__text {
    margin-top: 10px;
    font-size: 0.88rem;
}

/* ─────────────── PACKS: grid + card ─────────────── */
.mm-packs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
    gap: 12px;
    padding-bottom: 12px;
}

.mm-pack {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px 15px;
    border-radius: 14px;
    background: #ffffff;
    border: 1px solid #e8edf4;
    box-shadow: 0 10px 24px -20px rgba(15, 23, 42, 0.45);
    transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.2s, border-color 0.2s;
}

.mm-pack:hover {
    transform: translateY(-2px);
    border-color: #d7e0ec;
    box-shadow: 0 16px 30px -22px rgba(15, 23, 42, 0.55);
}

.mm-pack--off {
    background: #fbfcfd;
    border-style: dashed;
}

.mm-pack--off .mm-pack__name,
.mm-pack--off .mm-pack__remain-num {
    color: var(--mm-text-mute);
}

/* card header */
.mm-pack__head {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.mm-pack__icon {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    background: rgba(13, 44, 109, 0.07);
    color: var(--mm-navy);
}

.mm-pack__icon--call {
    background: rgba(63, 81, 181, 0.10);
    color: var(--mm-indigo);
}

.mm-pack__icon--sms {
    background: rgba(128, 102, 240, 0.10);
    color: var(--mm-purple);
}

.mm-pack__icon--data {
    background: rgba(22, 128, 75, 0.10);
    color: #16804b;
}

.mm-pack__titles {
    flex: 1;
    min-width: 0;
}

.mm-pack__name {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 800;
    color: var(--mm-text);
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.mm-pack__sub {
    display: block;
    margin-top: 2px;
    font-size: 0.75rem;
    color: var(--mm-text-mute);
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mm-pack__status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 800;
}

.mm-pack__status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
}

.mm-pack__status--on {
    background: rgba(22, 128, 75, 0.10);
    color: #16804b;
}

.mm-pack__status--off {
    background: rgba(148, 163, 184, 0.16);
    color: #64748b;
}

/* usage meter */
.mm-pack__meter-top {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 7px;
}

.mm-pack__remain {
    display: flex;
    align-items: baseline;
    gap: 5px;
    min-width: 0;
}

.mm-pack__remain-num {
    font-size: 1.35rem;
    font-weight: 800;
    line-height: 1;
    color: var(--mm-text);
    letter-spacing: -0.02em;
}

.mm-pack__remain-unit {
    font-size: 0.76rem;
    font-weight: 700;
    color: var(--mm-text-mute);
}

.mm-pack__pct {
    font-size: 0.82rem;
    font-weight: 800;
    padding: 2px 8px;
    border-radius: 8px;
}

.mm-pack__pct--good {
    background: rgba(22, 128, 75, 0.10);
    color: #16804b;
}

.mm-pack__pct--mid {
    background: rgba(192, 105, 28, 0.12);
    color: #c0691c;
}

.mm-pack__pct--low {
    background: rgba(199, 71, 71, 0.10);
    color: #c74747;
}

.mm-pack__pct--off {
    background: rgba(148, 163, 184, 0.16);
    color: #64748b;
}

.mm-pack__pct--bonus {
    background: rgba(128, 102, 240, 0.14);
    color: #5b45c7;
}

.mm-pack__bar {
    height: 8px;
    border-radius: 999px;
    background: #eef2f7;
    overflow: hidden;
}

.mm-pack__bar-fill {
    height: 100%;
    border-radius: 999px;
    transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.mm-pack__bar-fill--good {
    background: linear-gradient(90deg, #16804b, #22a563);
}

.mm-pack__bar-fill--mid {
    background: linear-gradient(90deg, #c0691c, #e2941f);
}

.mm-pack__bar-fill--low {
    background: linear-gradient(90deg, #c74747, #e06060);
}

.mm-pack__bar-fill--off {
    background: linear-gradient(90deg, #cbd5e1, #94a3b8);
}

.mm-pack__bar-fill--bonus {
    background: linear-gradient(90deg, #0d2c6d, #3f51b5 55%, #8066f0);
}

.mm-pack__meter-foot {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 7px;
    font-size: 0.76rem;
    font-weight: 600;
    color: var(--mm-text-soft);
}

.mm-pack__meter-foot .v-icon {
    color: var(--mm-text-mute) !important;
}

.mm-pack__bonus {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    margin-inline-start: auto;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(128, 102, 240, 0.12);
    color: #5b45c7;
    font-size: 0.72rem;
    font-weight: 800;
    white-space: nowrap;
}

.mm-pack__bonus .v-icon {
    color: #5b45c7 !important;
}

.mm-pack__meter--none {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 0;
    color: var(--mm-text-mute);
    font-size: 0.8rem;
    font-weight: 700;
}

/* card footer */
.mm-pack__foot {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 10px;
    border-top: 1px dashed #eef2f7;
}

.mm-pack__chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 999px;
    background: #f8fafc;
    border: 1px solid #eef2f7;
    color: var(--mm-text-soft);
    font-size: 0.74rem;
    font-weight: 700;
}

.mm-pack__chip .v-icon {
    color: var(--mm-text-mute) !important;
}

.mm-ltr-number input,
.mm-ltr-number .v-input__slot input {
    direction: ltr;
    text-align: left;
    letter-spacing: 0.02em;
}

::v-deep .v-text-field--outlined fieldset {
    border-color: #e3e8ef !important;
    transition: border-color 0.2s, box-shadow 0.2s;
}

::v-deep .v-text-field--outlined:hover fieldset {
    border-color: #c7d2e0 !important;
}

::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
    border-color: var(--mm-navy) !important;
    border-width: 2px !important;
    box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.06);
}

::v-deep .v-input .v-label,
::v-deep .v-input input,
::v-deep .v-input textarea,
::v-deep .v-select__selection {
    font-family: inherit;
}

::v-deep .v-input__prepend-inner {
    margin-top: 9px !important;
}

::v-deep .v-input__prepend-inner .v-icon {
    color: var(--mm-text-mute) !important;
}

::v-deep .v-input--is-focused .v-input__prepend-inner .v-icon {
    color: var(--mm-navy) !important;
}

.mm-dlg-foot {
    background: #ffffff;
    border-top: 1px solid var(--mm-border);
}

.mm-dlg-progress {
    display: block;
}

.mm-dlg-foot__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 22px;
}

.mm-dlg-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 9px 22px;
    border-radius: 10px;
    border: none;
    font-family: inherit;
    font-weight: 700;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.mm-dlg-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.mm-dlg-btn--ghost {
    background: transparent;
    color: var(--mm-text-soft);
    border: 1px solid #e3e8ef;
}

.mm-dlg-btn--ghost:hover:not(:disabled) {
    background: #f8fafc;
    color: var(--mm-text);
}

.mm-dlg-btn--primary {
    background: linear-gradient(135deg, var(--mm-navy) 0%, #142f78 100%);
    color: #fff;
    box-shadow: 0 6px 14px -4px rgba(13, 44, 109, 0.45);
}

.mm-dlg-btn--primary:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.08);
}

.mm-dlg-btn--danger {
    background: linear-gradient(135deg, #b42318 0%, #d92d20 100%);
    color: #fff;
    box-shadow: 0 6px 14px -4px rgba(180, 35, 24, 0.45);
}

.mm-dlg-btn--danger:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
}

.mm-delete-box {
    text-align: center;
    padding: 10px 0 18px;
}

.mm-delete-icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 14px;
    border-radius: 18px;
    background: #fff1f0;
    color: #d92d20;
    display: grid;
    place-items: center;
}

.mm-delete-title {
    margin: 0 0 8px;
    color: var(--mm-text);
    font-size: 1.05rem;
    font-weight: 800;
}

.mm-delete-text {
    margin: 0;
    color: var(--mm-text-soft);
    font-size: 0.9rem;
    line-height: 1.7;
}

/* ── טריגר עדכון דקות ── */
.mm-min-trigger {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-inline-start: auto;
    padding: 4px 11px;
    border-radius: 999px;
    border: 1px dashed #c7d2e0;
    background: #fff;
    color: var(--mm-navy);
    font-family: inherit;
    font-size: 0.74rem;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.18s ease;
}

.mm-min-trigger .v-icon {
    color: var(--mm-navy) !important;
}

.mm-min-trigger:hover {
    border-style: solid;
    border-color: var(--mm-navy);
    background: rgba(13, 44, 109, 0.05);
}

.mm-min-trigger--open {
    border-style: solid;
    background: var(--mm-navy);
    color: #fff;
}

.mm-min-trigger--open .v-icon {
    color: #fff !important;
}

/* ── פאנל עדכון דקות ── */
.mm-min {
    margin-top: 12px;
    padding: 13px;
    border: 1px solid var(--mm-border);
    border-radius: 12px;
    background: #f8fafc;
}

.mm-min__seg {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
    padding: 3px;
    border-radius: 10px;
    background: #eef2f7;
}

.mm-min__seg-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 7px 8px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--mm-text-soft);
    font-family: inherit;
    font-size: 0.8rem;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.18s ease;
}

.mm-min__seg-btn .v-icon {
    color: var(--mm-text-mute) !important;
}

.mm-min__seg-btn.is-on {
    background: #fff;
    color: #15803d;
    box-shadow: 0 2px 6px -2px rgba(15, 23, 42, 0.2);
}

.mm-min__seg-btn.is-on .v-icon {
    color: #15803d !important;
}

.mm-min__seg-btn--remove.is-on {
    color: #b42318;
}

.mm-min__seg-btn--remove.is-on .v-icon {
    color: #b42318 !important;
}

.mm-min__seg-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.mm-min__stepper {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 11px;
}

.mm-min__step {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    border-radius: 9px;
    border: 1px solid #e3e8ef;
    background: #fff;
    color: var(--mm-navy);
    cursor: pointer;
    display: grid;
    place-items: center;
    transition: all 0.18s ease;
}

.mm-min__step:hover:not(:disabled) {
    border-color: var(--mm-navy);
    background: rgba(13, 44, 109, 0.06);
}

.mm-min__step:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.mm-min__field {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 11px;
    border: 1px solid #e3e8ef;
    border-radius: 9px;
    background: #fff;
}

.mm-min__field input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: var(--mm-text);
    font-family: inherit;
    font-size: 1rem;
    font-weight: 800;
    direction: ltr;
    text-align: left;
}

.mm-min__field input::-webkit-outer-spin-button,
.mm-min__field input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.mm-min__field input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
}

.mm-min__field-unit {
    color: var(--mm-text-mute);
    font-size: 0.76rem;
    font-weight: 700;
}

.mm-min__quick {
    display: flex;
    gap: 6px;
    margin-top: 9px;
}

.mm-min__chip {
    flex: 1;
    padding: 5px 0;
    border: 1px solid #e3e8ef;
    border-radius: 8px;
    background: #fff;
    color: var(--mm-text-soft);
    font-family: inherit;
    font-size: 0.76rem;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.18s ease;
}

.mm-min__chip:hover:not(:disabled) {
    border-color: var(--mm-navy);
    color: var(--mm-navy);
}

.mm-min__chip.is-on {
    border-color: var(--mm-navy);
    background: rgba(13, 44, 109, 0.07);
    color: var(--mm-navy);
}

.mm-min__chip:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.mm-min__preview {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 11px;
    padding: 8px 10px;
    border-radius: 9px;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.5;
}

.mm-min__preview strong {
    font-weight: 900;
}

.mm-min__preview-note {
    font-style: normal;
    font-weight: 800;
    opacity: 0.8;
}

.mm-min__preview--add {
    background: #eefbf1;
    color: #15803d;
}

.mm-min__preview--add .v-icon {
    color: #15803d !important;
}

.mm-min__preview--remove {
    background: #fff1f0;
    color: #b42318;
}

.mm-min__preview--remove .v-icon {
    color: #b42318 !important;
}

.mm-min__actions {
    display: flex;
    justify-content: flex-end;
    gap: 7px;
    margin-top: 11px;
}

.mm-min__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 7px 16px;
    border: 1px solid transparent;
    border-radius: 9px;
    font-family: inherit;
    font-size: 0.8rem;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.18s ease;
}

.mm-min__btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.mm-min__btn--ghost {
    background: #fff;
    border-color: #e3e8ef;
    color: var(--mm-text-soft);
}

.mm-min__btn--ghost:hover:not(:disabled) {
    background: #f1f5f9;
}

.mm-min__btn--add {
    background: linear-gradient(135deg, #15803d 0%, #1a9648 100%);
    color: #fff;
    box-shadow: 0 5px 12px -4px rgba(21, 128, 61, 0.45);
}

.mm-min__btn--remove {
    background: linear-gradient(135deg, #b42318 0%, #d92d20 100%);
    color: #fff;
    box-shadow: 0 5px 12px -4px rgba(180, 35, 24, 0.45);
}

.mm-min__btn--add:hover:not(:disabled),
.mm-min__btn--remove:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.07);
}

@media (max-width: 600px) {
    .mm-dlg-head {
        padding: 14px 18px;
    }

    .mm-dlg-title {
        font-size: 1rem;
    }

    .mm-dlg-head__pill {
        display: none;
    }

    .mm-dlg-body {
        padding: 18px 18px 4px !important;
    }

    .mm-dlg-section {
        padding: 14px 14px 4px;
    }

    .mm-dlg-foot__actions {
        padding: 12px 16px;
    }

    .mm-dlg-btn {
        padding: 9px 16px;
        font-size: 0.85rem;
    }

    .mm-packs-grid {
        grid-template-columns: 1fr;
    }

    .mm-pack__remain-num {
        font-size: 1.2rem;
    }

    .mm-min-trigger {
        margin-inline-start: 0;
    }

    .mm-min__quick {
        flex-wrap: wrap;
    }
}
</style>